import { useEffect, useState, useCallback } from "react";
import { api, errText } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";
import { Plus, Trash2, BookOpen, Rocket, CheckCircle2, PencilLine } from "lucide-react";

const input = "w-full h-11 px-3 rounded-xl border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-600";
const label = "text-xs font-semibold uppercase tracking-wider text-slate-500";

function QuestionForm({ track, onAdded }) {
  const [form, setForm] = useState({ target: "exam", q_en: "", q_hi: "", answer: 0 });
  const [opts, setOpts] = useState(["", "", "", ""]);
  const submit = async () => {
    try {
      await api.post(`/instructor/tracks/${track.id}/questions`, { ...form, options_en: opts, options_hi: [] });
      toast.success("Question added");
      setForm({ ...form, q_en: "", q_hi: "" });
      setOpts(["", "", "", ""]);
      onAdded();
    } catch (e) { toast.error(errText(e.response?.data?.detail)); }
  };
  const filled = opts.filter((o) => o.trim());
  return (
    <div className="space-y-3 border-t border-slate-100 pt-4">
      <p className="font-semibold text-slate-800 text-sm">Add Question</p>
      <select data-testid="question-target-select" className={input} value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })}>
        <option value="exam">Final Exam</option>
        {track.lessons.map((l) => <option key={l.id} value={l.id}>Lesson quiz: {l.title.en}</option>)}
      </select>
      <input data-testid="question-en-input" className={input} placeholder="Question (English)" value={form.q_en} onChange={(e) => setForm({ ...form, q_en: e.target.value })} />
      <input data-testid="question-hi-input" className={input} placeholder="प्रश्न (Hindi, optional)" value={form.q_hi} onChange={(e) => setForm({ ...form, q_hi: e.target.value })} />
      <div className="grid grid-cols-2 gap-2">
        {opts.map((o, i) => (
          <input key={i} data-testid={`question-opt-${i}`} className={input} placeholder={`Option ${i + 1}`} value={o}
            onChange={(e) => { setOpts(opts.map((x, j) => (j === i ? e.target.value : x))); setForm((f) => ({ ...f, answer: 0 })); }} />
        ))}
      </div>
      <div className="flex items-center gap-3">
        <select data-testid="question-answer-select" className={`${input} w-auto`} value={form.answer} onChange={(e) => setForm({ ...form, answer: Number(e.target.value) })}>
          {filled.map((o, i) => <option key={i} value={i}>Correct: {o}</option>)}
        </select>
        <button data-testid="add-question-btn" onClick={submit} disabled={!form.q_en.trim() || filled.length < 2}
          className="h-11 px-5 rounded-xl bg-slate-900 text-white text-sm font-semibold disabled:opacity-40 flex items-center gap-1.5"><Plus size={16} /> Add</button>
      </div>
    </div>
  );
}

function CourseEditor({ trackId, onChanged }) {
  const [track, setTrack] = useState(null);
  const [lesson, setLesson] = useState({ title_en: "", title_hi: "", content_en: "", content_hi: "" });
  const { pick } = useLang();

  const load = useCallback(() => {
    api.get(`/tracks/${trackId}`).then((r) => setTrack(r.data)).catch(() => {});
  }, [trackId]);
  useEffect(() => { load(); }, [load]);
  if (!track) return <p className="text-slate-500">Loading…</p>;

  const addLesson = async () => {
    try {
      await api.post(`/instructor/tracks/${trackId}/lessons`, lesson);
      toast.success("Lesson added");
      setLesson({ title_en: "", title_hi: "", content_en: "", content_hi: "" });
      load(); onChanged();
    } catch (e) { toast.error(errText(e.response?.data?.detail)); }
  };
  const delLesson = async (lid) => {
    await api.delete(`/instructor/tracks/${trackId}/lessons/${lid}`);
    load(); onChanged();
  };
  const delExamQ = async (idx) => {
    await api.delete(`/instructor/tracks/${trackId}/exam/${idx}`);
    load(); onChanged();
  };

  return (
    <div className="space-y-4">
      <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
        <h2 className="font-heading font-bold text-slate-900 flex items-center gap-2"><BookOpen size={18} /> Lessons ({track.lessons.length})</h2>
        {track.lessons.map((l, i) => (
          <div key={l.id} className="flex items-center gap-3 p-3 rounded-xl border border-slate-100" data-testid={`builder-lesson-${l.id}`}>
            <span className="flex-1 text-sm font-medium text-slate-800">{i + 1}. {pick(l.title)} <span className="text-slate-400 font-normal">· {l.quiz.length} quiz Q</span></span>
            <button data-testid={`delete-lesson-${l.id}`} onClick={() => delLesson(l.id)} className="text-red-500 p-1.5"><Trash2 size={16} /></button>
          </div>
        ))}
        <div className="space-y-2 border-t border-slate-100 pt-4">
          <p className="font-semibold text-slate-800 text-sm">Add Lesson</p>
          <input data-testid="lesson-title-en-input" className={input} placeholder="Lesson title (English)" value={lesson.title_en} onChange={(e) => setLesson({ ...lesson, title_en: e.target.value })} />
          <input data-testid="lesson-title-hi-input" className={input} placeholder="पाठ शीर्षक (Hindi, optional)" value={lesson.title_hi} onChange={(e) => setLesson({ ...lesson, title_hi: e.target.value })} />
          <textarea data-testid="lesson-content-en-input" className="w-full rounded-xl border border-slate-300 p-3 text-sm" rows={4} placeholder="Lesson content (English). Use blank lines for paragraphs." value={lesson.content_en} onChange={(e) => setLesson({ ...lesson, content_en: e.target.value })} />
          <textarea data-testid="lesson-content-hi-input" className="w-full rounded-xl border border-slate-300 p-3 text-sm" rows={3} placeholder="पाठ सामग्री (Hindi, optional)" value={lesson.content_hi} onChange={(e) => setLesson({ ...lesson, content_hi: e.target.value })} />
          <button data-testid="add-lesson-btn" onClick={addLesson} disabled={!lesson.title_en.trim() || !lesson.content_en.trim()}
            className="h-11 px-5 rounded-xl bg-teal-700 text-white text-sm font-semibold disabled:opacity-40 flex items-center gap-1.5"><Plus size={16} /> Add Lesson</button>
        </div>
      </section>

      <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
        <h2 className="font-heading font-bold text-slate-900">Final Exam ({track.exam.length} questions)</h2>
        {track.exam.map((q, i) => (
          <div key={i} className="flex items-center gap-3 p-3 rounded-xl border border-slate-100" data-testid={`builder-exam-q-${i}`}>
            <span className="flex-1 text-sm text-slate-800">{i + 1}. {pick(q.q)}</span>
            <button data-testid={`delete-exam-q-${i}`} onClick={() => delExamQ(i)} className="text-red-500 p-1.5"><Trash2 size={16} /></button>
          </div>
        ))}
        <QuestionForm track={track} onAdded={() => { load(); onChanged(); }} />
      </section>
    </div>
  );
}

export default function Builder() {
  const [courses, setCourses] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ title_en: "", title_hi: "", desc_en: "", desc_hi: "", has_video: false, pass_pct: 70 });
  const [showCreate, setShowCreate] = useState(false);
  const { pick } = useLang();

  const load = useCallback(() => {
    api.get("/instructor/tracks").then((r) => setCourses(r.data)).catch(() => setCourses([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      const { data } = await api.post("/instructor/tracks", form);
      toast.success("Course created — now add lessons and exam questions");
      setForm({ title_en: "", title_hi: "", desc_en: "", desc_hi: "", has_video: false, pass_pct: 70 });
      setShowCreate(false);
      load();
      setSelected(data.id);
    } catch (e) { toast.error(errText(e.response?.data?.detail)); }
  };

  const publish = async (tid) => {
    try {
      await api.post(`/instructor/tracks/${tid}/publish`);
      toast.success("Published! Students can now enroll.");
      load();
    } catch (e) { toast.error(errText(e.response?.data?.detail)); }
  };

  if (!courses) return <p className="text-slate-500">Loading…</p>;
  const published = courses.filter((c) => c.published).length;
  const selectedCourse = courses.find((c) => c.id === selected);

  return (
    <div className="grid lg:grid-cols-[1fr,300px] gap-6 items-start">
    <div className="space-y-5 min-w-0">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900">Course Builder</h1>
        <button data-testid="new-course-btn" onClick={() => setShowCreate(!showCreate)}
          className="h-11 px-5 rounded-xl bg-teal-700 text-white text-sm font-semibold flex items-center gap-1.5 hover:bg-teal-800"><Plus size={16} /> New Course</button>
      </div>

      {showCreate && (
        <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="create-course-form">
          <p className={label}>Course details</p>
          <input data-testid="course-title-en-input" className={input} placeholder="Course title (English)" value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} />
          <input data-testid="course-title-hi-input" className={input} placeholder="कोर्स शीर्षक (Hindi, optional)" value={form.title_hi} onChange={(e) => setForm({ ...form, title_hi: e.target.value })} />
          <input data-testid="course-desc-en-input" className={input} placeholder="Short description (English)" value={form.desc_en} onChange={(e) => setForm({ ...form, desc_en: e.target.value })} />
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input data-testid="course-has-video-checkbox" type="checkbox" checked={form.has_video} onChange={(e) => setForm({ ...form, has_video: e.target.checked })} className="w-5 h-5 accent-teal-700" />
              Requires video practical
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">Pass mark %
              <input data-testid="course-pass-pct-input" type="number" min={1} max={100} value={form.pass_pct} onChange={(e) => setForm({ ...form, pass_pct: Number(e.target.value) })} className="w-20 h-10 px-2 rounded-lg border border-slate-300" />
            </label>
            <button data-testid="create-course-btn" onClick={create} disabled={!form.title_en.trim()}
              className="h-11 px-6 rounded-xl bg-slate-900 text-white text-sm font-semibold disabled:opacity-40">Create Course</button>
          </div>
        </section>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {courses.map((c) => (
          <div key={c.id} className={`bg-white rounded-2xl border p-4 space-y-2 ${selected === c.id ? "border-teal-500 ring-1 ring-teal-500" : "border-slate-200"}`} data-testid={`builder-course-${c.id}`}>
            <div className="flex items-center justify-between">
              <span className={`text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full ${c.published ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                {c.published ? "Published" : "Draft"}
              </span>
              <span className="text-xs text-slate-400">{c.lesson_count} lessons · {c.exam_count} exam Q</span>
            </div>
            <p className="font-heading font-bold text-slate-900 text-sm leading-snug">{pick(c.title)}</p>
            <div className="flex gap-2 pt-1">
              <button data-testid={`edit-course-${c.id}`} onClick={() => setSelected(selected === c.id ? null : c.id)}
                className="flex-1 h-10 rounded-xl border border-slate-300 text-slate-700 text-sm font-semibold flex items-center justify-center gap-1.5">
                <PencilLine size={15} /> {selected === c.id ? "Close" : "Edit"}
              </button>
              {!c.published ? (
                <button data-testid={`publish-course-${c.id}`} onClick={() => publish(c.id)}
                  className="flex-1 h-10 rounded-xl bg-emerald-600 text-white text-sm font-semibold flex items-center justify-center gap-1.5"><Rocket size={15} /> Publish</button>
              ) : (
                <span className="flex-1 h-10 rounded-xl bg-emerald-50 text-emerald-700 text-sm font-semibold flex items-center justify-center gap-1.5"><CheckCircle2 size={15} /> Live</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {selected && <CourseEditor trackId={selected} onChanged={load} />}
    </div>

    <aside className="space-y-4 hidden lg:block">
      <div className="bg-gradient-to-br from-teal-800 to-teal-950 rounded-2xl p-5 text-white space-y-3">
        <p className="font-heading font-bold">Your Studio</p>
        <div className="grid grid-cols-3 gap-2 text-center">
          {[[courses.length, "Courses"], [published, "Live"], [courses.length - published, "Drafts"]].map(([v, l]) => (
            <div key={l} className="bg-white/10 rounded-xl py-2.5"><p className="font-heading text-xl font-extrabold">{v}</p><p className="text-[10px] text-teal-200">{l}</p></div>
          ))}
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200/80 p-5 space-y-3 shadow-sm" data-testid="builder-checklist">
        <p className="font-heading font-bold text-slate-900 text-sm">Publishing Checklist</p>
        {[
          ["Course title & description", true],
          ["At least 1 lesson with content", !selectedCourse || selectedCourse.lesson_count > 0],
          ["Final exam question(s)", !selectedCourse || selectedCourse.exam_count > 0],
          ["Optional: lesson quick-check quizzes", true],
          ["Optional: Hindi translations", true],
        ].map(([label, done]) => (
          <p key={label} className="flex items-start gap-2 text-[13px]">
            <CheckCircle2 size={15} className={`shrink-0 mt-0.5 ${done ? "text-emerald-500" : "text-slate-300"}`} />
            <span className={done ? "text-slate-600" : "text-slate-400"}>{label}</span>
          </p>
        ))}
      </div>
      <div className="bg-amber-50 rounded-2xl border border-amber-200 p-5 space-y-2">
        <p className="font-heading font-bold text-amber-900 text-sm">Pro tips</p>
        <ul className="text-[12px] text-amber-800/90 space-y-1.5 list-disc pl-4">
          <li>Keep lessons under 5 minutes of reading — mobile learners finish more.</li>
          <li>Write questions that test decisions, not memory.</li>
          <li>Add Hindi titles — bilingual courses see far higher completion.</li>
          <li>Enable the video practical for any hands-on skill.</li>
        </ul>
      </div>
      <div className="rounded-2xl overflow-hidden border border-slate-200/80 shadow-sm">
        <img src="https://images.pexels.com/photos/5738735/pexels-photo-5738735.jpeg?auto=compress&cs=tinysrgb&w=600" alt="FSCD instructor" className="w-full h-36 object-cover" />
        <p className="text-[11px] text-slate-500 bg-white px-4 py-3">Your courses staff real FSCD vision centers and clinics across India.</p>
      </div>
    </aside>
    </div>
  );
}
